// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ShoppingCart, Heart, ShoppingBag } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import UserMenu from './UserMenu';
import { useCart } from '../context/CartContext';
import { authAPI, productAPI } from '../services/api';
import { translateCategory, getCategoryIcon } from '../utils/categoryTranslation';

/**
 * 統一的 Header 組件
 * 
 * @param {string} variant - 顯示模式
 *   - 'homepage': 首頁導航（白色背景 + 搜索 + 分類導航）
 *   - 'product': 商品頁導航（橙色背景 + 搜索 + 分類導航）- 用於商品列表和商品詳情
 *   - 'simple': 功能頁導航（白色背景 + 標題）- 用於購物車、訂單、收藏、個人資料
 * @param {string} selectedCategory - 當前選中的分類（僅在 product 模式下使用）
 * @param {function} onCategoryChange - 分類變更回調（僅在 product 模式下使用）
 * @param {string} title - 頁面標題（僅在 simple 模式下使用）
 */
const Header = ({ 
  variant = 'product', 
  selectedCategory = 'all',
  onCategoryChange = () => {},
  title = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount } = useCart();
  const [currentUser, setCurrentUser] = useState(null);
  const [categories, setCategories] = useState([]); // 新增：儲存分類數據
  const [loadingCategories, setLoadingCategories] = useState(false);

  // 新增：載入分類數據
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const result = await productAPI.getCategories();
        setCategories(result.categories || []);
      } catch (error) {
        console.error('載入分類失敗:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // 初始化時獲取用戶
  useEffect(() => {
    const updateUser = () => {
      const user = authAPI.getUser();
      setCurrentUser(user);
    };

    updateUser();

    // 監聽 storage 事件（跨標籤頁同步）
    window.addEventListener('storage', updateUser);
    
    return () => {
      window.removeEventListener('storage', updateUser);
    };
  }, [location.pathname]);

  // 監聽自定義的登入/登出事件
  useEffect(() => {
    const updateUser = () => {
      const user = authAPI.getUser();
      setCurrentUser(user);
    };

    window.addEventListener('userLoggedIn', updateUser);
    window.addEventListener('userLoggedOut', updateUser);
    
    return () => {
      window.removeEventListener('userLoggedIn', updateUser);
      window.removeEventListener('userLoggedOut', updateUser);
    };
  }, []);

  // 功能頁模式（白色背景 + 標題）
  // 適用於：購物車、訂單、收藏、個人資料等
  if (variant === 'simple') {
    return (
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')} 
              className="text-2xl font-bold text-orange-500 hover:text-orange-600 transition"
            >
              🛒 PG Mall
            </button>
            <div className="flex items-center gap-6">
              {title && (
                <div className="flex items-center gap-2 text-gray-600">
                  <ShoppingBag size={20} />
                  <span className="font-semibold">{title}</span>
                </div>
              )}
              <LanguageSwitcher />
              <UserMenu currentUser={currentUser} />
            </div>
          </div>
        </div>
      </header>
    );
  }

  // 首頁模式（白色背景 + 搜索 + 分類導航）
  if (variant === 'homepage') {
    return (
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <div className="text-2xl font-bold text-orange-500">🛒 PG Mall</div>
            </button>

            {/* 搜尋欄 */}
            <div className="flex-1 max-w-2xl mx-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('common.search')}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      console.log('Search:', e.target.value);
                    }
                  }}
                />
                <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* 右側功能區 */}
            <div className="flex items-center gap-4">
              <LanguageSwitcher />

              <button 
                onClick={() => navigate('/favorites')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
              >
                <Heart className="w-5 h-5" />
                <span className="text-sm hidden md:inline">{t('common.favorites')}</span>
              </button>

              <button 
                onClick={() => navigate('/cart')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 relative transition"
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="text-sm hidden md:inline">{t('common.cart')}</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
              </button>

              <UserMenu currentUser={currentUser} />
            </div>
          </div>

          {/* 動態分類導航 - 支持自動翻譯 */}
          <div className="border-t">
            <div className="flex items-center gap-6 py-3 overflow-x-auto scrollbar-hide">
              <button 
                onClick={() => navigate('/products')}
                className="text-sm text-gray-700 hover:text-orange-500 whitespace-nowrap transition"
              >
                {t('nav.allCategories')}
              </button>
              {loadingCategories ? (
                <span className="text-sm text-gray-400">{t('common.loading')}</span>
              ) : (
                categories.map((category) => (
                  <button 
                    key={category}
                    onClick={() => navigate(`/products?category=${encodeURIComponent(category)}`)}
                    className="text-sm text-gray-700 hover:text-orange-500 whitespace-nowrap transition"
                  >
                    {getCategoryIcon(category)} {translateCategory(category, t)}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // 商品頁模式（橙色背景 + 搜索 + 分類導航）
  // 適用於：商品列表頁和商品詳情頁
  return (
    <header className="bg-orange-500 text-white sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button 
            onClick={() => navigate('/')}
            className="text-2xl font-bold hover:text-orange-100 transition"
          >
            🛒 PG Mall
          </button>

          {/* 搜尋欄 */}
          <div className="flex-1 max-w-2xl mx-8">
            <div className="relative">
              <input
                type="text"
                placeholder={t('common.search')}
                className="w-full px-4 py-2 pl-10 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    console.log('Search:', e.target.value);
                  }
                }}
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* 右側功能區 */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            <button 
              onClick={() => navigate('/favorites')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-orange-600 transition"
            >
              <Heart className="w-5 h-5" />
              <span className="text-sm hidden md:inline">{t('common.favorites')}</span>
            </button>

            <button 
              onClick={() => navigate('/cart')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-orange-600 relative transition"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="text-sm hidden md:inline">{t('common.cart')}</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-white text-orange-500 text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </button>

            <UserMenu currentUser={currentUser} />
          </div>
        </div>

        {/* 動態分類導航 - 支持自動翻譯 */}
        <div className="border-t border-orange-400">
          <div className="flex items-center gap-6 py-3 overflow-x-auto scrollbar-hide">
            <button 
              onClick={() => {
                navigate('/products');
                onCategoryChange('all');
              }}
              className={`text-sm whitespace-nowrap transition ${
                selectedCategory === 'all' 
                  ? 'text-white font-semibold' 
                  : 'text-orange-100 hover:text-white'
              }`}
            >
              {t('nav.allCategories')}
            </button>
            {loadingCategories ? (
              <span className="text-sm text-orange-200">{t('common.loading')}</span>
            ) : (
              categories.map((category) => (
                <button 
                  key={category}
                  onClick={() => {
                    navigate(`/products?category=${encodeURIComponent(category)}`);
                    onCategoryChange(category);
                  }}
                  className={`text-sm whitespace-nowrap transition ${
                    selectedCategory === category 
                      ? 'text-white font-semibold' 
                      : 'text-orange-100 hover:text-white'
                  }`}
                >
                  {getCategoryIcon(category)} {translateCategory(category, t)}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;